import type React from 'react';
import { useTranslation } from 'react-i18next';
import InsurerLogo from '@/components/shared/InsurerLogo';
import { SupportedProductType } from '@/types/api';
import {
	formatCurrency,
	getInsurerName,
	getInsurerSlug,
	type ProductEntry
} from '../utils/productEntries';

interface PackageTableProps {
	/** The selected products, in display order. */
	entries: ProductEntry[];
	totalPremiumEur: number;
	/** Extra detail appended to the sick-leave row, e.g. waiting period and coverage. */
	coverageSummary?: string | null;
}

/**
 * The "Overzicht pakket" pricing table: one row per selected product with its
 * insurer logo, premium detail and annual premium, closed off by the total.
 * Shared by the customer's package overview and the read-only request overview.
 */
const PackageTable = ({
	entries,
	totalPremiumEur,
	coverageSummary = null
}: PackageTableProps): React.JSX.Element => {
	const { t } = useTranslation();

	return (
		<div className="border border-border rounded-lg overflow-hidden">
			{entries.map((entry, index) => {
				const slug = entry.selectedQuote ? getInsurerSlug(entry.selectedQuote) : null;
				// The logo is the row's only insurer identifier, so it needs a text
				// alternative rather than being treated as decorative.
				const insurerName = entry.selectedQuote ? getInsurerName(entry.selectedQuote) : null;
				const premiumDetailLabel = entry.premiumDetail
					? entry.key === SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE
						? t('productPackage.overview.amountPerEmployee', {
								amount: entry.premiumDetail
							})
						: t('productPackage.overview.premiumPercentage', {
								percentage: entry.premiumDetail
							})
					: null;
				const sickLeaveDetails =
					entry.key === SupportedProductType.SICK_LEAVE_SHORT_TERM
						? [premiumDetailLabel, coverageSummary].filter(Boolean).join(' · ')
						: null;

				return (
					<div
						key={entry.key}
						className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 px-5 py-6 bg-white sm:grid-cols-[7rem_minmax(0,1fr)_auto] sm:gap-7 sm:px-7 ${
							index < entries.length - 1 ? 'border-b border-border' : ''
						}`}
						data-testid="product-package-entry"
					>
						<div className="col-start-1 row-start-1 flex items-center justify-start min-w-0 sm:col-start-1 sm:row-start-1 sm:justify-center">
							{slug && (
								<div className="max-w-16 flex items-center justify-center sm:max-w-28">
									<InsurerLogo
										slug={slug}
										format="svg"
										alt={insurerName ?? ''}
										className="max-w-full max-h-full w-auto h-9 object-contain"
									/>
								</div>
							)}
						</div>

						<div className="col-start-1 row-start-2 min-w-0 sm:col-start-2 sm:row-start-1">
							<div className="min-w-0">
								<div className="my-1 text-base font-semibold text-foreground leading-snug sm:my-0">
									{entry.label}
								</div>
								{sickLeaveDetails && (
									<div
										className="text-xs text-muted-foreground tabular-nums mt-0.5"
										data-testid="product-package-premium-detail"
									>
										{sickLeaveDetails}
									</div>
								)}
								{entry.key !== SupportedProductType.SICK_LEAVE_SHORT_TERM && premiumDetailLabel && (
									<div
										className="text-xs text-muted-foreground tabular-nums mt-0.5"
										data-testid="product-package-premium-detail"
									>
										{premiumDetailLabel}
									</div>
								)}
							</div>
						</div>

						<div className="col-start-2 row-start-1 row-span-2 min-w-0 text-right self-center sm:col-start-3 sm:row-start-1 sm:row-span-1">
							{entry.premiumEur != null && (
								<span
									className="text-base font-bold text-foreground tabular-nums whitespace-nowrap sm:text-lg"
									data-testid="product-package-premium"
								>
									{formatCurrency(entry.premiumEur)}
								</span>
							)}
						</div>
					</div>
				);
			})}

			{entries.length > 0 && (
				<div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-5 py-4 bg-gray-50 border-t border-border">
					<div className="min-w-0">
						<div className="text-sm font-semibold text-foreground sm:text-base">
							{t('productPackage.total')}
						</div>
						<div className="text-xs text-muted-foreground mt-1">
							{t('productPackage.overview.employeeCompositionNote')}
						</div>
					</div>
					<span
						className="text-xl font-bold text-primary tabular-nums whitespace-nowrap sm:text-2xl"
						data-testid="product-package-total-premium"
					>
						{formatCurrency(totalPremiumEur)}
					</span>
				</div>
			)}
		</div>
	);
};

export default PackageTable;
